import React, { useState, useRef, useEffect, useCallback } from 'react';

export default function SplashScreen({ onComplete }) {
  const [opacity, setOpacity] = useState(0);
  const videoRef = useRef(null);
  const completedRef = useRef(false);

  const finish = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    // Fade out
    setOpacity(0);
    setTimeout(() => onComplete(), 900);
  }, [onComplete]);

  // Skip splash entirely on SSO callback or hash-based auth redirects
  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    if (
      path.includes('sso-callback') ||
      path.includes('callback') ||
      hash.includes('access_token') ||
      hash.includes('id_token')
    ) {
      // SSO redirect — skip splash immediately
      completedRef.current = true;
      onComplete();
      return;
    }
  }, [onComplete]);

  // Fade in on mount
  useEffect(() => {
    if (completedRef.current) return;
    const timer = requestAnimationFrame(() => {
      requestAnimationFrame(() => setOpacity(1));
    });
    return () => cancelAnimationFrame(timer);
  }, []);

  // Safety timeout — skip if video doesn't play/end within 8s
  useEffect(() => {
    if (completedRef.current) return;
    const safety = setTimeout(finish, 8000);
    return () => clearTimeout(safety);
  }, [finish]);

  const handleEnded = () => finish();
  const handleError = () => finish();

  // If already completed (SSO callback), render nothing
  if (completedRef.current) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity,
        transition: 'opacity 0.8s ease',
      }}
    >
      <video
        ref={videoRef}
        src="/videos/splash.mp4"
        autoPlay
        muted
        playsInline
        onEnded={handleEnded}
        onError={handleError}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    </div>
  );
}
